import React from 'react';
import { Check, Clock, RefreshCw, ShieldAlert, XCircle } from 'lucide-react';


const badgeClasses = {
  needs_verification: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  pending_review: 'bg-cyan-500/15 text-cyan-200 border-cyan-500/30',
  rejected: 'bg-red-500/15 text-red-300 border-red-500/30',
  approved: 'bg-green-500/15 text-green-300 border-green-500/30',
  pending: 'bg-pulse-elevated text-pulse-muted border-pulse-border',
};


const statusLabel = (status, lang) => {
  if (status === 'needs_verification') return lang === 'es' ? 'Necesita verificación' : 'Needs verification';
  if (status === 'pending_review') return lang === 'es' ? 'Revisión manual' : 'Pending review';
  if (status === 'rejected') return lang === 'es' ? 'Rechazada' : 'Rejected';
  if (status === 'approved') return lang === 'es' ? 'Aprobada' : 'Approved';
  return lang === 'es' ? 'Pendiente' : 'Pending';
};


export const AudioReviewPanel = ({ items, lang, stats, onDecision, onRerun, busySongId }) => {
  return (
    <div data-testid="audio-review-panel" className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {[
          { key: 'needs_verification', label: lang === 'es' ? 'Fingerprint con match' : 'Fingerprint matches', icon: ShieldAlert },
          { key: 'pending_review', label: lang === 'es' ? 'Revisión manual' : 'Manual review', icon: Clock },
          { key: 'rejected', label: lang === 'es' ? 'Rechazadas' : 'Rejected', icon: XCircle },
        ].map((item) => (
          <div key={item.key} data-testid={`audio-review-stat-${item.key}`} className="rounded-2xl border border-pulse-border bg-pulse-surface px-4 py-4">
            <div className="flex items-center gap-3">
              <item.icon size={16} className="text-pulse-primary" />
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-pulse-muted">{item.label}</p>
                <p className="text-2xl font-heading font-bold mt-1">{stats?.[item.key] || 0}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {items?.length === 0 && (
        <div data-testid="audio-review-empty" className="rounded-2xl border border-dashed border-pulse-border bg-pulse-surface px-5 py-10 text-center text-sm text-pulse-muted">
          {lang === 'es' ? 'No hay canciones en cola de revisión ahora mismo.' : 'There are no songs in the review queue right now.'}
        </div>
      )}

      {items?.map((song) => (
        <div key={song.id} data-testid={`audio-review-song-${song.id}`} className="rounded-3xl border border-pulse-border bg-pulse-surface p-5 space-y-4">
          <div className="flex flex-col lg:flex-row lg:items-center gap-4">
            <div className="flex items-center gap-4 min-w-0 flex-1">
              {song.cover ? (
                <img src={song.cover} alt={song.title_es} className="w-16 h-16 rounded-2xl object-cover border border-pulse-border" />
              ) : (
                <div className="w-16 h-16 rounded-2xl bg-pulse-elevated border border-pulse-border" />
              )}
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <h3 data-testid={`audio-review-song-title-${song.id}`} className="font-heading font-semibold text-lg truncate">{song.title_es || song.title_en}</h3>
                  <span data-testid={`audio-review-song-status-${song.id}`} className={`inline-flex items-center rounded-full border px-3 py-1 text-[11px] ${badgeClasses[song.status] || badgeClasses.pending}`}>
                    {statusLabel(song.status, lang)}
                  </span>
                </div>
                <p className="text-sm text-pulse-muted">{song.artist_name}</p>
                <p className="text-xs text-pulse-muted mt-1">
                  {lang === 'es' ? 'Subida por' : 'Uploaded by'} {song.uploader?.name || song.uploader?.email || '—'}
                </p>
              </div>
            </div>

            <div className="flex flex-wrap gap-3 text-sm">
              <div data-testid={`audio-review-song-trust-${song.id}`} className="rounded-2xl bg-pulse-elevated px-4 py-3 border border-pulse-border min-w-[120px]">
                <p className="text-[10px] uppercase tracking-[0.2em] text-pulse-muted">Trust score</p>
                <p className="text-xl font-bold mt-1">{song.trust_score ?? 0}</p>
              </div>
              <div className="rounded-2xl bg-pulse-elevated px-4 py-3 border border-pulse-border min-w-[180px]">
                <p className="text-[10px] uppercase tracking-[0.2em] text-pulse-muted">Providers</p>
                <p className="text-sm mt-1">{(song.verification?.linked_providers || []).join(', ') || (lang === 'es' ? 'Ninguno conectado' : 'No linked providers')}</p>
              </div>
            </div>
          </div>

          <div className="grid lg:grid-cols-[1.6fr,1fr] gap-4">
            <div className="rounded-2xl border border-pulse-border bg-pulse-elevated/70 p-4">
              <p className="text-[11px] uppercase tracking-[0.2em] text-pulse-muted mb-3">{lang === 'es' ? 'Razones del sistema' : 'System reasoning'}</p>
              <div className="space-y-2">
                {(song.verification?.summary || []).slice(0, 4).map((item, index) => (
                  <div key={`${song.id}-summary-${index}`} data-testid={`audio-review-song-summary-${song.id}-${index}`} className="text-sm text-white/90 flex gap-2">
                    <span className="mt-1 w-1.5 h-1.5 rounded-full bg-pulse-primary" />
                    <span>{item}</span>
                  </div>
                ))}
                {(song.verification?.summary || []).length === 0 && (
                  <p className="text-sm text-pulse-muted">{lang === 'es' ? 'Sin resumen adicional todavía.' : 'No additional summary yet.'}</p>
                )}
              </div>
            </div>

            <div className="rounded-2xl border border-pulse-border bg-pulse-elevated/70 p-4 space-y-3">
              <p className="text-[11px] uppercase tracking-[0.2em] text-pulse-muted">Fingerprint</p>
              <p className="text-sm">
                {song.verification?.fingerprint?.status === 'match'
                  ? (lang === 'es' ? 'ACRCloud detectó una referencia externa compatible.' : 'ACRCloud found a compatible external reference.')
                  : song.verification?.fingerprint?.status === 'no_match'
                    ? (lang === 'es' ? 'ACRCloud no encontró match.' : 'ACRCloud found no match.')
                    : (lang === 'es' ? 'Fingerprint en modo degradado o con error controlado.' : 'Fingerprint degraded or controlled error.')}
              </p>
              {song.verification?.fingerprint?.best_match?.title && (
                <div data-testid={`audio-review-fingerprint-match-${song.id}`} className="text-xs text-pulse-muted">
                  {song.verification.fingerprint.best_match.title} · {(song.verification.fingerprint.best_match.artists || []).join(', ')}
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              data-testid={`audio-review-approve-${song.id}`}
              onClick={() => onDecision(song.id, 'approved')}
              disabled={busySongId === song.id}
              className="rounded-2xl bg-green-500/15 border border-green-500/30 text-green-300 px-4 py-2 text-sm font-semibold disabled:opacity-50 inline-flex items-center gap-2"
            >
              <Check size={14} /> {lang === 'es' ? 'Aprobar' : 'Approve'}
            </button>
            <button
              data-testid={`audio-review-review-${song.id}`}
              onClick={() => onDecision(song.id, 'pending_review')}
              disabled={busySongId === song.id}
              className="rounded-2xl bg-cyan-500/15 border border-cyan-500/30 text-cyan-200 px-4 py-2 text-sm font-semibold disabled:opacity-50 inline-flex items-center gap-2"
            >
              <Clock size={14} /> {lang === 'es' ? 'Mandar a revisión' : 'Send to review'}
            </button>
            <button
              data-testid={`audio-review-reject-${song.id}`}
              onClick={() => onDecision(song.id, 'rejected')}
              disabled={busySongId === song.id}
              className="rounded-2xl bg-red-500/15 border border-red-500/30 text-red-300 px-4 py-2 text-sm font-semibold disabled:opacity-50 inline-flex items-center gap-2"
            >
              <XCircle size={14} /> {lang === 'es' ? 'Rechazar' : 'Reject'}
            </button>
            <button
              data-testid={`audio-review-rerun-${song.id}`}
              onClick={() => onRerun(song.id)}
              disabled={busySongId === song.id}
              className="rounded-2xl bg-pulse-elevated border border-pulse-border text-white px-4 py-2 text-sm font-semibold disabled:opacity-50 inline-flex items-center gap-2"
            >
              <RefreshCw size={14} /> {lang === 'es' ? 'Reintentar' : 'Rerun'}
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};